<template>
  <div :class="wrapperClass">
    <ul>
      <li v-for="(item, index) in fvrtsMenuList" :key="index" class="home">
        <button type="button" class="btn" @click="moveMenu(item)">{{ item.menuNm }}</button>
        <button type="button" class="delete" @click="handleFvrtsMenu('D', item)">삭제</button>
      </li>
    </ul>
    <button type="button" class="remote_btn" @click="onSendMessage">문자발송</button>
    <button type="button" class="favorite_btn" @click="handleFvrtsMenu('R')">즐겨찾는메뉴</button>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { procFvrtsMenu, getFvrtsMenuList } from "@/service/common.service";
import { useAlert } from "@/composables/useAlert";
import { useRoute, useRouter } from "vue-router";
import { useWindowPopup } from "@/composables/useWindowPopup";
import { useApiMenu } from "@/composables/useApiMenu";

const { confirm, success, error:toastError } = useAlert();
const { openPopup } = useWindowPopup();
const { menusWithLevel: navMenus } = useApiMenu();
const route = useRoute();
const router = useRouter();
const props = defineProps({
  /**
   * 'main' → favorite_menu_main
   * 'sub' (기본값) → favorite_menu
   */
  type: { type: String, default: 'sub' },
})

// 즐겨찾는메뉴 조회
const fvrtsMenuList = ref([])
const fetchFvrtsMenuList = async () => {
  try {
    const res = await getFvrtsMenuList();
    fvrtsMenuList.value = res.data || [];
  } catch (error) {
  }
}

onMounted(() => {
  fetchFvrtsMenuList();
});

// 즐겨찾는 메뉴 처리
const handleFvrtsMenu = async (processGbn, item) => {
  const targetMenuId = item?.menuId || route.query.menuId || ''
  let resolvedMenuId = targetMenuId

  if (processGbn === 'R') {
    // 등록 시 검증: menuId 없음
    if (!targetMenuId) {
      await toastError('등록할 메뉴 정보가 없습니다.')
      return
    }

    // 등록 시 검증: DIR(폴더) 메뉴면 하위 이동 가능 메뉴를 재귀 탐색하여 대체
    const menuInfo = navMenus.value.find(m => m.menuId === targetMenuId)
    if (menuInfo && menuInfo.menuType === 'DIR') {
      const findFirstLeafMenu = (parentId) => {
        const children = navMenus.value.filter(m => m.upMenuId === parentId)
        for (const child of children) {
          if (child.menuType !== 'DIR') return child
          const found = findFirstLeafMenu(child.menuId)
          if (found) return found
        }
        return null
      }
      const leafMenu = findFirstLeafMenu(targetMenuId)
      if (leafMenu) {
        resolvedMenuId = leafMenu.menuId
      } else {
        await toastError('등록 가능한 즐겨찾기 메뉴가 없습니다.')
        return
      }
    }

    // 등록 시 검증: 중복 등록 방지
    if (fvrtsMenuList.value.some(f => f.menuId === resolvedMenuId)) {
      await toastError('이미 즐겨찾기에 등록된 메뉴입니다.')
      return
    }
  }

  let content = '';
  if(processGbn === 'D') content = '선택한 메뉴를 즐겨찾기에서 제거하시겠습니까?';
  else content = '선택한 메뉴를 즐겨찾기에 추가하시겠습니까?';
  const confirmed = await confirm('즐겨찾기 메뉴 처리', '메뉴', {
    title: '즐겨찾는 메뉴 처리',
    content,
  });

  if (!confirmed) {
    return;
  }
  try {
    const params = {
      processGbn,
      menuId: processGbn === 'R' ? resolvedMenuId : (item?.menuId || targetMenuId),
      bkmkSn: item?.bkmkSn || '',
    }
    const res = await procFvrtsMenu(params);
    if (res?.result) {
      await success({
        title: '즐겨찾는 메뉴 처리 성공',
        content: '정상 처리되었습니다.',
      })
    } else {
      await toastError(res?.message || '즐겨찾는 메뉴 처리 중 오류가 발생했습니다.');
    }
    // 처리 후 목록 갱신
    await fetchFvrtsMenuList();

  } catch (error) {
    toastError(error?.serverMessage || error?.message || '즐겨찾는 메뉴 처리 중 오류가 발생했습니다.');
  }
}

// 메뉴 이동
const moveMenu = async (item) => {
  // item: menuId, menuNm, bkmkSn, menuType, menuLink
  if (!item) return

  // DIR(폴더) 메뉴는 이동 불가 → 삭제 안내
  if (item.menuType === 'DIR') {
    const confirmed = await confirm('즐겨찾기 메뉴', '메뉴', {
      title: '즐겨찾기 이동 불가',
      content: '해당 메뉴는 1차 메뉴(폴더)로 즐겨찾기에서 이동할 수 없습니다. 삭제하시겠습니까?',
      confirmText: '삭제',
      cancelText: '취소'
    })
    if (confirmed) {
      await handleFvrtsMenu('D', item)
    }
    return
  }

  const rawLink = item.menuLink || ''
  const lastSegment = rawLink.split('/').filter(Boolean).pop() || ''
  let path = rawLink

  if (item.menuType === 'BBS' && lastSegment) {
    path = `/board-mng/board/list/${lastSegment}`
  } else if (item.menuType === 'CONTENT' && lastSegment) {
    path = `/contents/view/${lastSegment}`
  }

  if (!path) return

  if (/^https?:\/\//i.test(path)) {
    window.open(path, '_blank')
    return
  }

  if (!path.startsWith('/')) {
    path = `/${path}`
  }

  router.push(path).catch((err) => {
    toastError(err?.serverMessage || err?.message || '메뉴 이동 중 오류가 발생했습니다.');
  })
}

// computed 대신 간단한 조건 삼항
const wrapperClass = computed(() =>
    props.type === 'main' ? 'favorite_menu_main' : 'favorite_menu'
)

// 문자발송 팝업 열기
function onSendMessage() {
  openPopup('/resv-mng/sms-send-popup', {
    name: 'smsSendPopup',
    width: 1100,
    height: 800
  })
}
</script>

<style scoped>
</style>
